import React, { Component } from 'react'
import PropTypes from 'prop-types'
import MediaQuery from 'react-responsive'
import OrderDetail from 'components/OrderDetail'
import ReactGA from 'react-ga'
import { connect } from 'react-redux'
import { withRouter, Link, Redirect } from 'react-router-dom'
import 'react-credit-cards/es/styles-compiled.css'
import 'static/css/CardPayment.css'
import 'static/css/MastercardSDK.css'
import { Button, Well } from '@yapay/design-system'
import CustomClickToPayOtpInput from 'components/CustomClickToPayOtpInput'
import AlternateAuthModal from 'components/AlternateAuthModal'
import { allowClickToPay, getRemember, changeUser } from 'actions'
import imgCardBlue from 'static/img/card-blue.svg'

const COUNTRY_CODE_BRAZIL = '55'
const initialState = {
  btnText: 'Continuar com Click to Pay',
  otpValue: '',
  maskedEmail: '',
  maskedPhone: '',
  messageSentTo: '',
  loading: false,
  errorMessage: '',
  showAlternateAuthModal: false,
}

export class CardPaymentWalletAuthentication extends Component {
  state = initialState

  submitForm = event => {
    event.preventDefault()

    if (this.state.otpValue.length !== 6) {
      this.setState({ errorMessage: 'Código inválido' })
      return
    }

    this.setState({ loading: true, btnText: 'Processando...' })

    this.validate()
      .then(response => {
        this.verifyPath()
      })
      .catch(error => {
        let errorReturned = ''
        switch (error?.details?.reason) {
          case 'CODE_INVALID':
            errorReturned = 'Código inválido, tente novamente.'
            break
          case 'RETRIES_EXCEEDED':
            errorReturned =
              'Número de tentativas excedido. Tente novamente mais tarde ou entre com o cartão manualmente.'
            break
          case 'ACCT_INACCESSIBLE':
            errorReturned =
              'Número de tentativas excedido. Tente novamente mais tarde ou entre com o cartão manualmente.'
            break
          default:
            errorReturned = 'Algo deu errado. Tente novamente.'
            break
        }
        this.setState({
          loading: false,
          btnText: 'Continuar com Click to Pay',
          errorMessage: errorReturned,
        })
      })
  }

  initializeClick2Pay = async () => {
    if (window.mcCheckoutService) {
      return null
    }

    // eslint-disable-next-line no-undef
    window.mcCheckoutService = new MastercardCheckoutServices()

    const recognitionToken = await getRemember()

    const params = {
      srcDpaId: process.env.REACT_APP_MASTERCARD_SRC_DPA_ID,
      dpaData: { dpaName: this.props.seller.name },
      checkoutExperience: 'WITHIN_CHECKOUT',
      dpaTransactionOptions: {
        consumerNameRequested: true,
        confirmPayment: false,
        dpaLocale: 'pt_BR',
        paymentOptions: [
          { dynamicDataType: 'CARD_APPLICATION_CRYPTOGRAM_SHORT_FORM' },
        ],
      },
      cardBrands: ['mastercard', 'visa'],
    }

    if (recognitionToken) {
      params.recognitionToken = recognitionToken
    }

    await window.mcCheckoutService.init(params)
  }

  idLookUp = async () => {
    try {
      const {
        phone,
        email,
        modifiedWithWalletEmail,
        modifiedWithWalletPhone,
      } = this.props.customer

      if (modifiedWithWalletEmail) {
        return await window.mcCheckoutService.idLookup({
          email: modifiedWithWalletEmail,
        })
      }

      if (modifiedWithWalletPhone) {
        return await this.idLookUpByPhone(modifiedWithWalletPhone)
      }

      const phoneResult = await this.idLookUpByPhone(phone)

      return phoneResult.consumerPresent
        ? phoneResult
        : await window.mcCheckoutService.idLookup({ email })
    } catch {
      return { consumerPresent: false }
    }
  }

  idLookUpByPhone = phone => {
    return window.mcCheckoutService.idLookup({
      mobileNumber: {
        countryCode: COUNTRY_CODE_BRAZIL,
        phoneNumber: phone.replace(/\D/g, ''),
      },
    })
  }

  initiateValidation = async (validationChannelId = null) => {
    this.setState({ loading: true })

    let validationResponse = null
    if (!validationChannelId) {
      validationResponse = await window.mcCheckoutService.initiateValidation()
    } else {
      validationResponse = await window.mcCheckoutService.initiateValidation({
        requestedValidationChannelId: validationChannelId,
      })
    }

    this.setState({ loading: false })
    return validationResponse
  }

  getPhoneFromValidationResponse = validationResponse => {
    const smsChannel = validationResponse?.supportedValidationChannels?.find(
      channel => channel.identityType === 'SMS',
    )
    return smsChannel?.maskedValidationChannel || this.state.maskedPhone
  }

  getEmailFromValidationResponse = validationResponse => {
    const emailChannel = validationResponse?.supportedValidationChannels?.find(
      channel => channel.identityType === 'EMAIL',
    )
    return emailChannel?.maskedValidationChannel || this.state.maskedEmail
  }

  resendCodeCellphone = async () => {
    this.setState({ loading: true })

    try {
      const validationResponse = await this.initiateValidation('SMS')
      const maskedPhone = this.getPhoneFromValidationResponse(
        validationResponse,
      )

      this.setState({
        loading: false,
        messageSentTo: `número ${maskedPhone}`,
      })
    } catch {
      this.setState({
        loading: false,
        errorMessage: 'Erro ao reenviar código.',
      })
    }
  }

  resendCodeEmail = async () => {
    this.setState({ loading: true })

    try {
      const validationResponse = await this.initiateValidation('EMAIL')
      const maskedEmail = this.getEmailFromValidationResponse(
        validationResponse,
      )

      this.setState({
        loading: false,
        messageSentTo: `e-mail ${maskedEmail}`,
      })
    } catch {
      this.setState({
        loading: false,
        errorMessage: 'Erro ao reenviar código.',
      })
    }
  }

  validate = async () => {
    this.setState({ errorMessage: '' })
    const validateResponse = await window.mcCheckoutService.validate({
      value: this.state.otpValue,
    })

    return validateResponse
  }

  maskPhone = phone => {
    const digits = phone.replace(/\D/g, '')
    const lastTwoDigits = digits.substring(digits.length - 2)
    return `${COUNTRY_CODE_BRAZIL} (••) •••••-••${lastTwoDigits}`
  }

  maskEmail = email => {
    const firstLetter = email.substring(0, 1)
    const lastLetterBeforeAt = email.substring(
      email.indexOf('@') - 1,
      email.indexOf('@'),
    )
    const allAfterAt = email.substring(email.indexOf('@'), email.length)
    return `${firstLetter}.....${lastLetterBeforeAt}${allAfterAt}`
  }

  verifyPath() {
    const redirectPath = allowClickToPay(this.props.seller)
      ? '/identificar-usuario-do-click-to-pay'
      : '/pagamento-cartao'
    this.props.history.replace(redirectPath)
  }

  componentDidMount() {
    const passedPhoneNumber = this.props.location?.state?.phoneNumber
    const passedEmailAddress = this.props.location?.state?.emailAddress

    if (passedPhoneNumber || passedEmailAddress) {
      this.handleAlternateAuthSubmit(passedPhoneNumber, passedEmailAddress)
      return
    }

    const maskedPhone = this.maskPhone(this.props.customer.phone)
    const maskedEmail = this.maskEmail(this.props.customer.email)

    this.setState({
      loading: true,
      maskedPhone,
      maskedEmail,
      messageSentTo: `número ${maskedPhone}`,
    })

    this.initializeClick2Pay()
      .then(async () => {
        const cards = await window.mcCheckoutService.getCards().catch(() => [])
        if (Array.isArray(cards) && cards.length > 0) {
          this.setState({ loading: false })
          this.verifyPath()
          return
        }

        const idLookUpResponse = await this.idLookUp()
        if (!idLookUpResponse.consumerPresent) {
          this.verifyPath()
          return
        }

        await this.initiateValidation().finally(() => {
          this.setState({
            loading: false,
          })
        })
      })
      .catch(() => {
        this.props.history.push('/pagamento-cartao')
      })
  }

  setOtpValue = value => {
    this.setState({ otpValue: value })
  }

  handleNotYouRequested = () => {
    this.setState({ showAlternateAuthModal: true })
  }

  handleAlternateAuthSubmit = async (phoneNumber, emailAddress) => {
    this.setState({
      showAlternateAuthModal: false,
      otpValue: '',
      loading: true,
      errorMessage: '',
    })

    try {
      const wasUpdated = this.updateEmailOrPhone(emailAddress, phoneNumber)
      if (!wasUpdated) {
        this.setState({ loading: false })
        return
      }

      await this.initializeClick2Pay()

      const idLookUpResponse = await this.idLookUp()
      if (!idLookUpResponse.consumerPresent) {
        this.setState({
          loading: false,
          errorMessage:
            'Email ou telefone não encontrado no Click to Pay. Por favor, verifique ou entre com o cartão manualmente.',
        })
        return
      }

      const validationChannel = emailAddress ? 'EMAIL' : 'SMS'
      await this.initiateValidation(validationChannel).finally(() => {
        this.setState({ loading: false })
      })
    } catch {
      this.setState({
        loading: false,
        errorMessage:
          'Erro ao processar. Por favor, tente novamente ou entre com o cartão manualmente.',
      })
    }
  }

  updateEmailOrPhone = (email = null, phone = null) => {
    if (email) {
      this.props.dispatch(
        changeUser({
          modifiedWithWalletEmail: email,
        }),
      )

      this.setState({
        maskedEmail: this.maskEmail(email),
        messageSentTo: `e-mail ${this.maskEmail(email)}`,
      })
      return true
    }

    if (phone) {
      this.props.dispatch(
        changeUser({
          modifiedWithWalletPhone: phone,
        }),
      )

      this.setState({
        maskedPhone: this.maskPhone(phone),
        messageSentTo: `número ${this.maskPhone(phone)}`,
      })
      return true
    }

    return false
  }

  render() {
    ReactGA.event({
      category: 'Wallet Authentication',
      action: 'Card',
    })

    if (this.props.orderUndefined) {
      return <Redirect to="/nao-encontrado" />
    }

    return (
      <>
        <form
          onSubmit={this.submitForm}
          id="payment-form"
          data-yapay="payment-form"
        >
          <div
            className="flex items-center"
            style={{ marginBottom: '16px', gap: '8px' }}
          >
            <img src={imgCardBlue} alt="logo" />
            <h2 style={{ color: '#4F4886', margin: '0px' }}>
              Cartão de crédito
            </h2>
          </div>

          <div className="otp-container">
            {this.state.loading ? (
              <src-loader locale="pt_BR"></src-loader>
            ) : (
              <CustomClickToPayOtpInput
                otpValue={this.state.otpValue}
                onOtpChange={this.setOtpValue}
                maskedEmail={this.state.maskedEmail}
                maskedPhone={this.state.maskedPhone}
                onResendCellphone={this.resendCodeCellphone}
                onResendEmail={this.resendCodeEmail}
                onNotYouRequested={this.handleNotYouRequested}
                errorMessage={this.state.errorMessage}
                messageSentTo={this.state.messageSentTo}
              />
            )}
          </div>

          <MediaQuery query="(max-width: 767px)">
            <Well className="p-4 md:p-4 -mx-4" plain>
              <OrderDetail />
            </Well>
          </MediaQuery>

          <br />

          <div
            id="buttons-container"
            style={{ display: this.state.loading ? 'none' : 'block' }}
          >
            <Button
              className="w-full"
              color="info"
              disabled={this.state.otpValue.length !== 6}
            >
              {this.state.btnText}
            </Button>
            <br />
            <br />

            <Link to="/pagamento-cartao" className="button w-full text-center">
              Entrar com o cartão manualmente
            </Link>
            <br />
            <br />
            <div className="text-center">
              <Link
                to="/escolha-pagamento"
                className="button w-full md:w-auto"
                style={{ border: 'none' }}
              >
                Voltar
              </Link>
            </div>
          </div>
        </form>

        <AlternateAuthModal
          isOpen={this.state.showAlternateAuthModal}
          onClose={() => this.setState({ showAlternateAuthModal: false })}
          onSubmit={this.handleAlternateAuthSubmit}
          loading={this.state.loading}
        />
      </>
    )
  }
}

CardPaymentWalletAuthentication.propTypes = {
  orderUndefined: PropTypes.bool.isRequired,
  history: PropTypes.object,
  dispatch: PropTypes.func,
  customer: PropTypes.object,
  seller: PropTypes.object,
}

const mapStateToProps = /* istanbul ignore next */ state => {
  const {
    transaction: { orderUndefined, seller },
  } = state

  const customer = {
    email: state.user.email,
    phone: state.user.phone,
    modifiedWithWalletPhone: state.user.modifiedWithWalletPhone || null,
    modifiedWithWalletEmail: state.user.modifiedWithWalletEmail || null,
  }

  return {
    orderUndefined,
    customer,
    seller,
  }
}

export default withRouter(
  connect(mapStateToProps)(CardPaymentWalletAuthentication),
)
